import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AlertTriangle, ArrowRight, Check, X, Loader2, Pin, ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const severityColor = {
  high: 'border-rose-300 bg-rose-50/40',
  medium: 'border-amber-300 bg-amber-50/40',
  low: 'border-slate-200 bg-slate-50/40',
};

const severityBadge = {
  high: 'bg-rose-100 text-rose-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-slate-200 text-slate-700',
};

const PathLine = ({ l1, l2, l3, color = 'text-slate-700' }) => (
  <span className={`font-mono text-xs ${color}`}>
    {l1 || '—'} <span className="text-slate-400">›</span> {l2 || '—'} <span className="text-slate-400">›</span> {l3 || '—'}
  </span>
);

const DriftAnomalyCard = ({ anomaly, token, uploadId, onResolved }) => {
  const [submitting, setSubmitting] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const handleResolve = async (action) => {
    setSubmitting(action);
    try {
      const res = await axios.post(
        `${API}/data-refinery/drift-detect/${uploadId}/resolve`,
        { supplier: anomaly.supplier, action },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const msg = action === 'pin_historical'
        ? `Pinned ${anomaly.supplier} to historical path. ${res.data.records_updated} records updated; rule saved for future uploads.`
        : action === 'accept_current'
        ? `Accepted new path for ${anomaly.supplier}; rule saved.`
        : `Dismissed ${anomaly.supplier} anomaly.`;
      toast.success(msg);
      onResolved(anomaly.supplier, action);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Resolution failed');
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <div className={`border rounded-sm overflow-hidden ${severityColor[anomaly.severity] || severityColor.low}`}
         data-testid={`drift-card-${anomaly.supplier}`}>
      <div className="p-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${severityBadge[anomaly.severity]}`}>
                {anomaly.severity.toUpperCase()} drift
              </span>
              <h3 className="text-sm font-heading font-semibold text-slate-900 truncate">
                {anomaly.supplier}
              </h3>
            </div>
            <div className="space-y-1 text-xs">
              <div className="flex items-baseline gap-2">
                <span className="text-slate-500 w-20 flex-shrink-0">This upload:</span>
                <PathLine {...anomaly.current} l1={anomaly.current.l1} l2={anomaly.current.l2} l3={anomaly.current.l3} color="text-rose-700" />
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-slate-500 w-20 flex-shrink-0">Historical:</span>
                <PathLine l1={anomaly.historical.l1} l2={anomaly.historical.l2} l3={anomaly.historical.l3} color="text-emerald-700" />
              </div>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-slate-500">
              <span>This upload: <strong className="text-slate-700">{anomaly.current.total_records}</strong> rec / Rs {anomaly.current.total_spend.toLocaleString(undefined, {maximumFractionDigits:0})}</span>
              <span>Historical: <strong className="text-slate-700">{anomaly.historical.total_records}</strong> rec across {anomaly.historical.sessions} session(s)</span>
            </div>
          </div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-slate-400 hover:text-slate-600 flex-shrink-0"
            data-testid={`drift-expand-${anomaly.supplier}`}
          >
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>

        {expanded && (
          <div className="text-xs text-slate-600 bg-white border border-slate-200 rounded-sm p-3 mb-3 space-y-1.5">
            <p>
              <strong>Pin to historical</strong> overwrites this upload&apos;s records for{' '}
              <code className="px-1 bg-slate-100">{anomaly.supplier}</code> back to the historical path
              and creates a permanent classification rule (priority 10) so every future upload
              auto-routes this supplier the same way.
            </p>
            <p>
              <strong>Accept new path</strong> keeps this upload&apos;s records as-is but creates a
              rule pinning the new path — useful if the supplier genuinely changed what they sell.
            </p>
            <p>
              <strong>Dismiss</strong> takes no action and just acknowledges the anomaly.
            </p>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            onClick={() => handleResolve('pin_historical')}
            disabled={!!submitting}
            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
            data-testid={`drift-pin-${anomaly.supplier}`}
          >
            {submitting === 'pin_historical' ? <Loader2 size={14} className="animate-spin" /> : <Pin size={14} />}
            Pin to historical
            <Sparkles size={10} className="text-amber-300" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleResolve('accept_current')}
            disabled={!!submitting}
            className="border-slate-300 text-slate-700 gap-1.5"
            data-testid={`drift-accept-${anomaly.supplier}`}
          >
            {submitting === 'accept_current' ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            Accept new path
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleResolve('dismiss')}
            disabled={!!submitting}
            className="border-slate-200 text-slate-500 gap-1.5"
            data-testid={`drift-dismiss-${anomaly.supplier}`}
          >
            {submitting === 'dismiss' ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />}
            Dismiss
          </Button>
        </div>
      </div>
    </div>
  );
};

export const SupplierDriftBanner = ({ token, uploadId, sessionStatus }) => {
  const [anomalies, setAnomalies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resolvedSet, setResolvedSet] = useState(new Set());
  const [expanded, setExpanded] = useState(true);

  const fetchAnomalies = useCallback(async () => {
    if (!uploadId || sessionStatus !== 'classified') return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/data-refinery/drift-detect/${uploadId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAnomalies(res.data.anomalies || []);
      setResolvedSet(new Set());
    } catch (err) {
      // silent — anomaly check is non-blocking
    } finally {
      setLoading(false);
    }
  }, [uploadId, sessionStatus, token]);

  useEffect(() => { fetchAnomalies(); }, [fetchAnomalies]);

  const handleResolved = (supplier) => {
    setResolvedSet(prev => new Set([...prev, supplier]));
  };

  const pending = anomalies.filter(a => !resolvedSet.has(a.supplier));

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-3 bg-slate-50 border border-slate-200 rounded-sm text-sm text-slate-600"
           data-testid="drift-banner-loading">
        <Loader2 size={14} className="animate-spin" />
        Scanning for supplier classification drift…
      </div>
    );
  }

  if (anomalies.length === 0) return null;
  if (pending.length === 0) {
    return (
      <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-sm text-sm text-emerald-700"
           data-testid="drift-banner-cleared">
        <Check size={14} className="inline mr-1" />
        All {anomalies.length} supplier drift anomalies resolved. Safe to export.
      </div>
    );
  }

  const highCount = pending.filter(a => a.severity === 'high').length;
  const medCount = pending.filter(a => a.severity === 'medium').length;

  return (
    <div className="border-l-4 border-rose-500 bg-rose-50/30 rounded-sm" data-testid="supplier-drift-banner">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-start justify-between gap-3 p-4 text-left hover:bg-rose-50/60 transition-colors"
        data-testid="drift-banner-toggle"
      >
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <AlertTriangle size={20} className="text-rose-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-rose-900">
              {pending.length} supplier drift anomaly(ies) detected — review before export
            </p>
            <p className="text-xs text-rose-700 mt-1">
              Suppliers whose L1/L2/L3 in this upload differs from their historical classification.
              {highCount > 0 && ` ${highCount} HIGH (L1 changed)`}
              {medCount > 0 && ` · ${medCount} MEDIUM (L2 changed)`}.
              One-click pin to historical to lock supplier routing across all future uploads.
            </p>
          </div>
        </div>
        <span className="text-rose-600 flex-shrink-0">
          {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </span>
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-2" data-testid="drift-anomalies-list">
          {pending.map(a => (
            <DriftAnomalyCard
              key={a.supplier}
              anomaly={a}
              token={token}
              uploadId={uploadId}
              onResolved={handleResolved}
            />
          ))}
        </div>
      )}
    </div>
  );
};
